import { useRef, useState } from "react";
import axios from "axios";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Check, Undo2 } from "lucide-react";
import { DictationMic } from "@/components/DictationMic";

export const REPHRASE_STYLES = [
  ["tidy", "Tidy up"],
  ["shorter", "Make it shorter"],
  ["formal", "More formal"],
  ["friendly", "Friendlier"],
];

// The resident portal rides its own HttpOnly cookie, so it can't use the staff axios instance.
const residentCfg = () => ({ withCredentials: true });

// The style picker on its own — for fields that already have their own tuned rephrase call
// (broadcasts, knowledge entries) and just need the four choices.
export function RephraseStyleMenu({ onPick, busy, testId = "rephrase", label = "AI rephrase" }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button type="button" data-testid={`${testId}-btn`} disabled={busy} onClick={() => setOpen((o) => !o)}
        title="Rephrase with AI — every fact stays exactly the same"
        className="inline-flex items-center gap-1 rounded-full bg-redline-50 px-2.5 py-1 text-[11px] font-semibold text-redline-600 transition-colors hover:bg-redline-100 disabled:opacity-60">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
        {busy ? "Rephrasing…" : label}
      </button>
      {open && !busy && (
        <div data-testid={`${testId}-menu`}
          className="absolute right-0 top-full z-50 mt-1.5 w-44 overflow-hidden rounded-xl border border-sand-200 bg-white py-1 shadow-xl">
          {REPHRASE_STYLES.map(([k, l]) => (
            <button key={k} type="button" data-testid={`${testId}-style-${k}`}
              onClick={() => { setOpen(false); onPick(k); }}
              className="block w-full px-3 py-2 text-left text-xs font-semibold text-ink/70 hover:bg-sand-50 hover:text-ink">
              {l}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

// Mic + "Rephrase with AI" for any text box.
// Dictation appends to what's already there (and transcribes whatever language you speak).
// A rephrase NEVER lands on its own — the writer reads it first, accepts it, and can undo.
export function TextAssist({
  value, onChange, surface = "general", disabled, resident = false,
  testId = "text-assist", micTitle = "Dictate", className = "", align = "center", mic = true,
}) {
  // Panels have to stay reachable, so they open toward the room available: centred on the button
  // by default (safe inside a narrow dialog), or pinned left/right where the control sits at the
  // edge of its row — a preview you can't click is worse than no preview.
  const side = align === "left" ? "left-0"
    : align === "right" ? "right-0"
      : "left-1/2 -translate-x-1/2";
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState("");
  const [draft, setDraft] = useState(null);
  const [prev, setPrev] = useState(null);
  const [up, setUp] = useState(false);
  const anchor = useRef(null);
  const v = value || "";

  // Open upward when there isn't room below, so "Use this" is never stranded under the fold.
  const place = (needed = 260) => {
    const r = anchor.current?.getBoundingClientRect();
    setUp(!!r && window.innerHeight - r.bottom < needed && r.top > needed);
  };
  const vpos = up ? "bottom-full mb-1.5" : "top-full mt-1.5";

  const run = async (style) => {
    setMenu(false);
    if (!v.trim()) { toast.error("Type or dictate something first"); return; }
    setBusy(style);
    place();
    try {
      const path = resident ? "/rephrase/resident" : "/rephrase";
      const body = { text: v, style, surface };
      const { data } = resident
        ? await axios.post(`${API}${path}`, body, residentCfg())
        : await api.post(path, body);
      setDraft({ style, text: data.text });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rephrase that");
    }
    setBusy("");
  };

  const accept = () => { setPrev(v); onChange(draft.text); setDraft(null); };
  const undo = () => { onChange(prev); setPrev(null); toast.success("Put your wording back"); };

  return (
    <span ref={anchor} className={`relative inline-flex items-center gap-0.5 ${className}`}>
      {mic && <DictationMic
        testId={`${testId}-mic`}
        disabled={disabled}
        title={micTitle}
        endpoint={resident ? "/help/transcribe/resident" : "/transcribe"}
        post={resident ? (path, fd) => axios.post(`${API}${path}`, fd, residentCfg()) : undefined}
        onText={(t) => onChange((v.trim() ? v.trim() + " " : "") + t)}
      />}
      {prev !== null && !draft && (
        <button type="button" data-testid={`${testId}-undo`} onClick={undo} title="Undo the rephrase"
          className="inline-flex h-7 items-center gap-1 rounded-full px-2 text-xs font-bold text-ink/45 hover:bg-sand-100 hover:text-ink">
          <Undo2 className="h-3.5 w-3.5" /> Undo
        </button>
      )}
      <button type="button" data-testid={`${testId}-rephrase`} disabled={disabled || !!busy}
        onClick={() => { place(190); setMenu((m) => !m); }} title="Rephrase with AI"
        className="inline-flex h-7 shrink-0 items-center justify-center rounded-full px-1.5 text-ink/40 transition-colors hover:bg-redline-50 hover:text-redline-600 disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={1.75} />}
      </button>

      {menu && !draft && (
        <div data-testid={`${testId}-menu`}
          className={`absolute ${side} ${vpos} z-50 w-44 overflow-hidden rounded-xl border border-sand-200 bg-white py-1 shadow-xl`}>
          {REPHRASE_STYLES.map(([k, label]) => (
            <button key={k} type="button" data-testid={`${testId}-style-${k}`} onClick={() => run(k)}
              className="block w-full px-3 py-2 text-left text-xs font-semibold text-ink/70 hover:bg-sand-50 hover:text-ink">
              {label}
            </button>
          ))}
        </div>
      )}

      {draft && (
        <div data-testid={`${testId}-preview`}
          className={`absolute ${side} ${vpos} z-50 w-[19rem] max-w-[calc(100vw-2rem)] rounded-xl border border-sand-200 bg-white p-3 shadow-xl sm:w-[22rem]`}>
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-ink/40">
            {REPHRASE_STYLES.find(([k]) => k === draft.style)?.[1]} — your words stay until you say so
          </div>
          <div data-testid={`${testId}-preview-text`}
            className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg bg-sand-50 p-2.5 text-xs leading-relaxed text-ink/80">
            {draft.text}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <button type="button" data-testid={`${testId}-accept`} onClick={accept}
              className="inline-flex items-center gap-1.5 rounded-full bg-redline-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-redline-600">
              <Check className="h-3.5 w-3.5" /> Use this
            </button>
            <button type="button" data-testid={`${testId}-reject`} onClick={() => setDraft(null)}
              className="rounded-full border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/55 hover:border-ink/30">
              Keep mine
            </button>
            <span className="ml-auto flex items-center gap-0.5">
              {REPHRASE_STYLES.filter(([k]) => k !== draft.style).map(([k, label]) => (
                <button key={k} type="button" data-testid={`${testId}-retry-${k}`} onClick={() => run(k)}
                  className="rounded-full px-1.5 py-1 text-[11px] font-semibold text-ink/40 hover:bg-sand-100 hover:text-ink">
                  {label.replace("Make it ", "").replace("More ", "")}
                </button>
              ))}
            </span>
          </div>
        </div>
      )}
    </span>
  );
}
